import { API_ORIGIN, authorizedFetch, parseApiErrorMessage } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'


export type SlideRow = {
  id: string
  order: number
  html: string
  css: string
  created_at: string
  updated_at: string
}

export type SlideDeckListItem = {
  id: string
  title: string
  public_slug: string
  status: 'draft' | 'published'
  slide_count: number
  updated_at: string
  created_at: string
}

export type SlideDeck = {
  id: string
  title: string
  public_slug: string
  status: 'draft' | 'published'
  slides: SlideRow[]
  updated_at: string
  created_at: string
}

export type PublicSlideDeck = {
  title: string
  public_slug: string
  slides: SlideRow[]
}

export type SlideWrite = {
  id?: string
  order?: number
  html?: string
  css?: string
}

async function fetchDecks(): Promise<SlideDeckListItem[]> {
  const response = await authorizedFetch('/api/slides/')
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, 'Could not load slides'))
  }
  return (await response.json()) as SlideDeckListItem[]
}

async function fetchDeck(id: string): Promise<SlideDeck> {
  const response = await authorizedFetch(`/api/slides/${id}`)
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, 'Deck not found'))
  }
  return (await response.json()) as SlideDeck
}

export function useSlideDecks(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['slides'],
    queryFn: fetchDecks,
    enabled: options?.enabled !== false,
  })
}

export function useSlideDeck(deckId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['slide-deck', deckId],
    queryFn: () => fetchDeck(deckId!),
    enabled: Boolean(deckId) && options?.enabled !== false,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useCreateSlideDeck() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (title?: string) => {
      const response = await authorizedFetch('/api/slides/', {
        method: 'POST',
        body: JSON.stringify({ title: title ?? '' }),
      })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Could not create deck'))
      }
      return (await response.json()) as SlideDeck
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['slides'] })
    },
  })
}

export function useUpdateSlideDeck(deckId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { title?: string; slides?: SlideWrite[] }) => {
      const response = await authorizedFetch(`/api/slides/${deckId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Could not save deck'))
      }
      return (await response.json()) as SlideDeck
    },
    onSuccess: (deck) => {
      queryClient.setQueryData(['slide-deck', deck.id], deck)
      void queryClient.invalidateQueries({ queryKey: ['slides'] })
    },
  })
}

export function usePublishSlideDeck(deckId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await authorizedFetch(`/api/slides/${deckId}/publish`, { method: 'POST' })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Could not publish'))
      }
      return (await response.json()) as SlideDeck
    },
    onSuccess: (deck) => {
      queryClient.setQueryData(['slide-deck', deck.id], deck)
      void queryClient.invalidateQueries({ queryKey: ['slides'] })
    },
  })
}

export async function fetchPublicSlideDeck(slug: string): Promise<PublicSlideDeck> {
  const response = await fetch(`${API_ORIGIN}/api/slides/public/${encodeURIComponent(slug)}`)
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, 'Deck not found'))
  }
  return (await response.json()) as PublicSlideDeck
}
